import { STEP_1_FIELDS, STEP_2_FIELDS, STEP_3_FIELDS } from '../keys';

const STEP_1_FIELDS_SET = new Set(Object.values(STEP_1_FIELDS));
const STEP_2_FIELDS_SET = new Set(Object.values(STEP_2_FIELDS));
const STEP_3_FIELDS_SET = new Set(Object.values(STEP_3_FIELDS));

const toCamelCase = (str: string): string =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()).replace(/^[A-Z]/, (c) => c.toLowerCase());

const toCamelCasePathPart = (part: string | number): string => {
  return typeof part === 'number' ? `[${part}]` : toCamelCase(part);
};

const transformFieldPath = (path: string | string[]) => {
  if (Array.isArray(path)) {
    return path.map(toCamelCasePathPart).join('.').replace(/\.\[/g, '[');
  }

  return path
    .replace(/\.+$/, '')
    .replace(/\.(\d+)\./g, '[$1].')
    .replace(/\.(\d+)$/, '[$1]')
    .split('.')
    .map(toCamelCase)
    .join('.');
};

const getBaseKey = (fieldPath: string): string => {
  const match = fieldPath.match(/^([^[.]+)/);
  return match ? match[1] : fieldPath;
};

// Backend may return errors for fields that don't exist in the form (e.g. transliterated variants).
// Map them to the closest visible form field so the error is still surfaced to the user.
const FIELD_ALIASES: Record<string, string> = {
  transliteratedRegisteredAddress: 'registeredAddress',
  transliteratedPhysicalAddress: 'physicalAddress',
  transliteratedFirstName: 'firstName',
  transliteratedLastName: 'lastName',
};

const applyFieldAliases = (fieldPath: string): string => {
  const baseKey = getBaseKey(fieldPath);
  const alias = FIELD_ALIASES[baseKey];
  if (!alias) return fieldPath;
  return alias + fieldPath.slice(baseKey.length);
};

const mapBackendErrorsToFormFields = (backendError: any) => {
  if (!backendError?.data?.meta?.failedAttributes) return {};

  const errors: Record<string, string> = {};
  backendError.data.meta.failedAttributes.forEach(({ path, message }: any) => {
    const fieldPath = applyFieldAliases(transformFieldPath(path));
    errors[fieldPath] = message;
  });

  return errors;
};

// Shared helper: parses a failedAttributes array from any backend error response,
// applies all normalisation (camelCase, trailing dots, field aliases) and stores
// results under an optional field prefix (e.g. "beneficiaries[0]").
export const parseFailedAttributes = (
  failedAttributes: Array<{ path: string | string[]; message: string }>,
  fieldPrefix?: string,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  failedAttributes.forEach(({ path, message }) => {
    const normalised = applyFieldAliases(transformFieldPath(path));
    const key = fieldPrefix ? `${fieldPrefix}.${normalised}` : normalised;
    errors[key] = message;
  });
  return errors;
};

// Handles the Bridge "err.generic" error schema where errors are returned as
// meta.source.key — a plain object mapping field paths to messages.
export const parseSourceKey = (sourceKey: Record<string, string>, fieldPrefix?: string): Record<string, string> => {
  const errors: Record<string, string> = {};
  Object.entries(sourceKey).forEach(([path, message]) => {
    const normalised = applyFieldAliases(transformFieldPath(path));
    const key = fieldPrefix ? `${fieldPrefix}.${normalised}` : normalised;
    errors[key] = message;
  });
  return errors;
};

export const getStepWithError = (backendError: Record<string, string>) => {
  const lowestStep = Object.keys(backendError).reduce((lowest, keyName) => {
    const formattedKey = getBaseKey(keyName);
    let step = Infinity;
    if (STEP_1_FIELDS_SET.has(formattedKey)) {
      step = 1;
    } else if (STEP_2_FIELDS_SET.has(formattedKey)) {
      step = 2;
    } else if (STEP_3_FIELDS_SET.has(formattedKey)) {
      step = 3;
    }

    if (step < lowest) {
      return step;
    }

    return lowest;
  }, Infinity);

  return lowestStep === Infinity ? null : lowestStep;
};

export default mapBackendErrorsToFormFields;
